import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { DraftService } from './draft.service';
import { UserService } from './user.service';
import { AppStateService } from './app-state.service';
import { DraftData, DraftInfo } from '../models/product.model';

/**
 * Album Design area — pagination-only pre-drafts (draft_kind = 1). Same draft_orders
 * data as the normal drafts area, scoped by the backend to DraftKind::ALBUM_DESIGN and
 * exposed through dedicated routes:
 *   - GET/DELETE/PATCH  /api/album-design[/{id}][/rename]
 *   - POST              /api/upload/album-design-bytes[/{id}]   (create/update)
 *
 * The upload endpoint reuses the SAME backend service (uploadDraftOrderBytes) as normal
 * drafts, so the multipart contract is identical — we reuse DraftService's form-data
 * builder verbatim and only swap the URL. Loading the .order body is also generic, so we
 * delegate to DraftService.loadDraft.
 *
 * Every area route is gated server-side by the `hasAlbumDesign` flag; when the flag is
 * revoked between logins the backend returns 403 {"error":"ALBUM_DESIGN_DISABLED"}. We
 * centralize that here: refresh the cached user and leave the area.
 *
 * Graduation ("Iniciar encomenda") — saving one of these through the NORMAL draft-order
 * endpoint flips draft_kind to 0 server-side and the draft leaves this area — is a Phase 2
 * concern and is not handled here.
 */
@Injectable({ providedIn: 'root' })
export class AlbumDesignService {
  private readonly api = inject(ApiService);
  private readonly draftService = inject(DraftService);
  private readonly userService = inject(UserService);
  private readonly appState = inject(AppStateService);
  private readonly router = inject(Router);

  /** List the user's Album Design drafts. Omitting `limit` returns all items. */
  list(year?: number, page?: number, limit?: number): Observable<DraftInfo[]> {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    const path = qs ? `/album-design?${qs}` : '/album-design';
    return this.api.get<any>(path).pipe(
      map(response => {
        const items = response?.items || (Array.isArray(response) ? response : []);
        const drafts: DraftInfo[] = items.map((item: any) => this.draftService.mapDraftInfo(item));
        return drafts;
      }),
      catchError(err => this.handleAreaError(err))
    );
  }

  /** Fetch a single Album Design draft's metadata (includes the .order `url`). */
  get(draftId: number): Observable<DraftInfo> {
    return this.api.get<any>(`/album-design/${draftId}`).pipe(
      map(item => this.draftService.mapDraftInfo(item)),
      catchError(err => this.handleAreaError(err))
    );
  }

  /** Load the draft's `.order` body (generic — reuses the normal draft loader). */
  loadDraft(draftInfo: DraftInfo): Observable<DraftData> {
    return this.draftService.loadDraft(draftInfo);
  }

  /**
   * Create (no id) or update (id) an Album Design draft. Reuses DraftService's
   * form-data builder so the field contract matches the backend exactly; only the
   * URL differs. The server stamps draft_kind from the route — never send it.
   */
  saveDraft(
    draftName: string,
    draftData: DraftData,
    productTypeId: number,
    draftId?: number
  ): Observable<DraftInfo> {
    const formData = this.draftService.buildDraftOrderFormData(draftName, draftData, productTypeId, draftId, false);
    const path = draftId && draftId > 0
      ? `/upload/album-design-bytes/${draftId}`
      : '/upload/album-design-bytes';
    return this.api.upload<DraftInfo>(path, formData).pipe(
      catchError(err => this.handleAreaError(err))
    );
  }

  /** Soft-delete an Album Design draft. */
  delete(draftId: number): Observable<boolean> {
    return this.api.delete<{ success?: boolean }>(`/album-design/${draftId}`).pipe(
      map(data => data?.success === true),
      catchError(err => this.handleAreaError(err))
    );
  }

  /** Rename an Album Design draft. */
  rename(draftId: number, newName: string): Observable<boolean> {
    return this.api.patch<{ success?: boolean; id?: number }>(`/album-design/${draftId}/rename`, { name: newName }).pipe(
      // The area rename returns the updated draft ({ id, ... }); older shape was { success }.
      // A 2xx reaching here already means success (non-2xx routes to the error channel).
      map(data => !!data && (data.success === true || data.id != null)),
      catchError(err => this.handleAreaError(err))
    );
  }

  /** True for the 403 the backend returns when the feature flag is off/revoked. */
  private isAccessRevoked(err: any): boolean {
    return err?.status === 403
      && (err?.error?.error === 'ALBUM_DESIGN_DISABLED' || err?.error === 'ALBUM_DESIGN_DISABLED');
  }

  /**
   * Centralized area-error handling: on ALBUM_DESIGN_DISABLED the flag was revoked
   * between logins — refresh the cached user (so the nav entry disappears) and leave
   * the area. Always rethrow so callers can still stop their own spinners.
   */
  private handleAreaError(err: any): Observable<never> {
    if (this.isAccessRevoked(err)) {
      this.userService.checkSession();
      this.router.navigate(this.appState.buildLocaleCommands(['home']));
    }
    return throwError(() => err);
  }
}
